import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, ChevronDown, MapPin, Ruler, Wallet, Layers, MessageSquare } from 'lucide-react';
import api, { LOCATIONS, PROJECT_TYPES, FINISH_LEVELS, trackEvent, formatZARFull } from '@/lib/api';
import { motion } from 'framer-motion';

const HERO_BG = "/projects/hero.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_feasibility-hub-10/artifacts/nb8rpfwp_Ruam%20Architects%20Logo.jpg";

const PORTFOLIO_IMAGES = [
  { src: "/projects/house-bosman.jpg", title: "House Bosman", location: "Pretoria", style: "Residential" },
  { src: "/projects/house-malan.jpg", title: "House Malan", location: "Pretoria", style: "Residential" },
  { src: "/projects/house-claasen.jpg", title: "House Claasen", location: "Pretoria", style: "Residential" },
];

export default function Home() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ project_type: '', location: '', stand_size: '', budget: '' });
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    trackEvent('page_view', { page: 'home' });
    api.get('/projects').then(r => setProjects(r.data.filter(p => p.featured))).catch(() => {});
  }, []);

  const handleQuickCheck = () => {
    if (!form.project_type || !form.location || !form.stand_size || !form.budget) return;
    trackEvent('hero_feasibility_click', form);
    const params = new URLSearchParams(form);
    navigate(`/feasibility?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* ========== HERO ========== */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Modern luxury villa" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-12 sm:pt-28 sm:pb-20 min-h-screen flex items-center">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center w-full">
            {/* Left Typography */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <img src={LOGO_URL} alt="Raum Architects" className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover ring-2 ring-brand-accent/40 shadow-xl shadow-black/40" />
                <div>
                  <span className="text-white font-heading text-sm tracking-[0.15em] uppercase block font-medium">Raum Architects</span>
                  <span className="text-brand-accent font-heading text-xs italic tracking-wide">We create space</span>
                </div>
              </div>
              <h1
                className="font-heading tracking-tighter font-medium text-white mb-5"
                style={{
                  // Fluid scale: 36px → 72px between 320px and 1280px viewports.
                  fontSize: 'clamp(2.5rem, 7.5vw, 4.5rem)',
                  lineHeight: 1.05,
                }}
              >
                We Create<br />
                <span className="text-brand-gradient">Space.</span>
              </h1>
              <p className="text-white/60 text-base sm:text-sm font-heading italic mb-6 tracking-wide">
                Before anything is built.
              </p>
              <p className="text-white/70 text-base sm:text-base lg:text-lg font-body leading-relaxed max-w-lg mb-4">
                Most architecture begins with uncertainty. What can I afford? Is my site feasible?
                What should I even ask an architect?
              </p>
              <p className="text-white/70 text-base sm:text-base lg:text-lg font-body leading-relaxed max-w-lg mb-10">
                We share the answers to these questions before your first meeting.
                These insights are normally discussed in a first consultation.
                Here, they are yours immediately.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/feasibility" data-testid="hero-cta-feasibility" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-white text-brand-dark hover:bg-gray-200 rounded-sm h-12 px-8 font-body text-sm font-semibold uppercase tracking-wider">
                    Is My Project Possible? <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/cost-calculator" data-testid="hero-cta-cost" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 rounded-sm h-12 px-8 font-body text-sm font-semibold uppercase tracking-wider">
                    What Will It Really Cost?
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right - Glassmorphic Feasibility Tool */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="glass-card rounded-sm p-5 sm:p-7 w-full"
              data-testid="hero-feasibility-card"
            >
              <h3 className="font-heading text-lg font-medium text-white mb-1">Is This Possible?</h3>
              <p className="text-white/40 text-sm font-body mb-6">Understand whether your idea can work on your site and budget</p>

              <div className="space-y-4 dark-input">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5">
                      <Layers className="w-3 h-3" /> Type
                    </Label>
                    <Select value={form.project_type} onValueChange={(v) => setForm(p => ({ ...p, project_type: v }))}>
                      <SelectTrigger data-testid="hero-select-type" className="bg-white/5 border-white/20 text-white rounded-sm h-10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" /> Location
                    </Label>
                    <Select value={form.location} onValueChange={(v) => setForm(p => ({ ...p, location: v }))}>
                      <SelectTrigger data-testid="hero-select-location" className="bg-white/5 border-white/20 text-white rounded-sm h-10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {LOCATIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5">
                      <Ruler className="w-3 h-3" /> Stand Size (m²)
                    </Label>
                    <Input
                      data-testid="hero-input-stand"
                      type="number"
                      value={form.stand_size}
                      onChange={(e) => setForm(p => ({ ...p, stand_size: e.target.value }))}
                      placeholder="e.g. 800"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30 rounded-sm h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1.5">
                      <Wallet className="w-3 h-3" /> Budget (R)
                    </Label>
                    <Input
                      data-testid="hero-input-budget"
                      type="number"
                      value={form.budget}
                      onChange={(e) => setForm(p => ({ ...p, budget: e.target.value }))}
                      placeholder="e.g. 5000000"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/30 rounded-sm h-10"
                    />
                  </div>
                </div>
              </div>

              <Button
                data-testid="hero-submit-btn"
                onClick={handleQuickCheck}
                disabled={!form.project_type || !form.location || !form.stand_size || !form.budget}
                className="w-full mt-6 bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm h-11 font-body font-semibold text-sm uppercase tracking-wider disabled:opacity-40"
              >
                See What This Unlocks <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-6 h-6 text-white/30" />
        </div>
      </section>

      {/* ========== PORTFOLIO ========== */}
      <section className="section-light py-16 sm:py-20 lg:py-24 px-6" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 sm:mb-16">
            <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Selected Work</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-brand-dark">
              Space, Material, Light, Context
            </h2>
            <p className="text-brand-dark/60 font-body text-base sm:text-sm mt-3 max-w-xl leading-relaxed">
              Each project begins with understanding — the site, the people, the purpose.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(projects.length > 0 ? projects.slice(0, 3) : PORTFOLIO_IMAGES).map((project, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
                className="project-card group relative aspect-[4/5] overflow-hidden rounded-sm"
                data-testid={`portfolio-card-${i}`}
              >
                <img
                  src={project.images?.[0] || project.src}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-brand-accent text-xs font-bold uppercase tracking-wider mb-1.5 font-heading">
                    {project.location === 'cape_town' ? 'Cape Town' : project.location?.charAt(0).toUpperCase() + project.location?.slice(1) || project.location}
                  </p>
                  <h3 className="font-heading text-xl font-medium text-white">{project.title}</h3>
                  <p className="text-white/50 text-sm font-body mt-1">{project.style}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY THIS EXISTS ========== */}
      <section className="bg-white py-20 px-6" data-testid="why-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-4">Why This Exists</p>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-brand-dark mb-6">
            Most clients only learn this after weeks of back-and-forth
          </h2>
          <p className="text-brand-dark/50 font-body text-base leading-relaxed max-w-2xl mx-auto mb-4">
            Architecture begins with uncertainty. What can I afford? Is my site feasible? What should I even ask?
            These are the questions that normally take multiple conversations to answer.
          </p>
          <p className="text-brand-dark/50 font-body text-base leading-relaxed max-w-2xl mx-auto">
            We believe informed clients make better projects. So we share the answers now — grounded in real data,
            guided by professional experience, and interpreted with care. Not to replace your architect, but to prepare you for one.
          </p>
        </div>
      </section>

      {/* ========== TOOLS SECTION ========== */}
      <section className="bg-brand-light py-16 sm:py-20 lg:py-24 px-6" data-testid="tools-section">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 sm:mb-16">
            <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em] mb-3">Guided Decision Tools</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-brand-dark">
              Clarity Before Commitment
            </h2>
            <p className="text-brand-dark/60 font-body text-base mt-4 max-w-2xl leading-relaxed">
              Each tool addresses a specific question that every building project faces. The answers include architect-guided insights
              that explain what the numbers mean, what trade-offs are implied, and what to think about next.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feasibility */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group bg-white border border-black/10 rounded-sm p-8 hover:border-brand-accent/30 transition-all hover:shadow-lg"
              data-testid="tool-cta-feasibility"
            >
              <div className="w-10 h-10 rounded-sm bg-brand-accent/10 flex items-center justify-center mb-5">
                <MapPin className="w-5 h-5 text-brand-accent-dark" />
              </div>
              <h3 className="font-heading text-xl font-medium mb-2 text-brand-dark">Is This Possible?</h3>
              <p className="text-brand-dark/50 text-sm font-body mb-6 leading-relaxed">
                Understand whether your idea can realistically work on your site and budget.
                Based on comparable projects and local conditions.
              </p>
              <Link to="/feasibility" data-testid="tool-link-feasibility">
                <Button className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                  Understand Your Options <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Cost */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="group bg-white border border-black/10 rounded-sm p-8 hover:border-brand-accent/30 transition-all hover:shadow-lg"
              data-testid="tool-cta-cost"
            >
              <div className="w-10 h-10 rounded-sm bg-brand-accent/10 flex items-center justify-center mb-5">
                <Wallet className="w-5 h-5 text-brand-accent-dark" />
              </div>
              <h3 className="font-heading text-xl font-medium mb-2 text-brand-dark">What Will This Really Cost?</h3>
              <p className="text-brand-dark/50 text-sm font-body mb-6 leading-relaxed">
                Real-world cost ranges including architecture fees, engineering, and council approvals.
                No surprises. No false precision.
              </p>
              <Link to="/cost-calculator" data-testid="tool-link-cost">
                <Button className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                  See the Full Picture <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </motion.div>

            {/* Brief Builder */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="group bg-white border border-black/10 rounded-sm p-8 hover:border-brand-accent/30 transition-all hover:shadow-lg"
              data-testid="tool-cta-brief"
            >
              <div className="w-10 h-10 rounded-sm bg-brand-accent/10 flex items-center justify-center mb-5">
                <Layers className="w-5 h-5 text-brand-accent-dark" />
              </div>
              <h3 className="font-heading text-xl font-medium mb-2 text-brand-dark">Turn Ideas Into a Clear Brief</h3>
              <p className="text-brand-dark/50 text-sm font-body mb-6 leading-relaxed">
                Structure your thinking so an architect can respond meaningfully.
                Most clients arrive with a vision but not yet a brief.
              </p>
              <Link to="/brief-builder" data-testid="tool-link-brief">
                <Button className="bg-brand-dark text-white hover:bg-gray-800 rounded-sm font-body text-xs font-semibold uppercase tracking-wider">
                  Start Your Brief <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========== ASK AN ARCHITECT ========== */}
      <section className="bg-brand-light py-12 sm:py-16 px-6" data-testid="ask-architect-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-sm border border-black/10 p-6 sm:p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-brand-accent/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-brand-accent-dark" />
                </div>
                <p className="text-brand-accent-dark font-heading text-xs font-bold uppercase tracking-[0.2em]">Architect Guidance</p>
              </div>
              <h3 className="font-heading text-2xl md:text-2xl font-medium text-brand-dark mb-3">
                Ask an Architect
              </h3>
              <p className="text-brand-dark/60 text-base sm:text-sm font-body leading-relaxed max-w-md">
                Have questions about budgets, processes, or what to expect? Get clear, professional guidance — grounded in South African architecture practice.
              </p>
            </div>
            <Link to="/ask-architect" data-testid="home-ask-architect-link" className="w-full md:w-auto">
              <Button className="w-full md:w-auto bg-brand-dark text-white hover:bg-gray-800 rounded-sm h-11 px-8 font-body text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Start a Conversation <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ========== AGENTIC ARCHITECT CTA ========== */}
      <section className="bg-brand-dark py-16 sm:py-20 px-6" data-testid="architect-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-brand-accent/70 font-heading italic text-sm mb-4">We don't automate architecture. We clarify decisions before they become expensive.</p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-white mb-4">
            Ready to Prepare Properly?
          </h2>
          <p className="text-white/60 font-body text-base sm:text-base lg:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Our guided pre-design consultation captures your spatial needs, lifestyle priorities, and constraints. It creates a structured foundation that saves your architect time and saves you money.
          </p>
          <Link to="/architect" data-testid="home-architect-cta" className="inline-block w-full sm:w-auto">
            <Button className="w-full sm:w-auto bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm h-12 px-10 font-body text-sm font-semibold uppercase tracking-wider">
              Begin Guided Consultation <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ========== STATS ========== */}
      <section className="section-dark py-14 sm:py-20 px-6" data-testid="stats-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '15+', label: 'Years Experience' },
              { value: '100+', label: 'Projects Completed' },
              { value: '9', label: 'Provinces Served' },
              { value: 'SACAP', label: 'Registered' },
            ].map((stat, i) => (
              <div key={i} data-testid={`stat-${i}`}>
                <p className="font-heading text-3xl sm:text-4xl font-medium text-brand-accent mb-1">{stat.value}</p>
                <p className="text-white/60 text-sm font-body">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
